<template>
  <section class="bg-[#fcf5ec] py-16">
    <div class="w-full grid lg:grid-cols-[2fr_1fr] gap-0 bg-[#EEDCC5]">
      <!-- FORM -->
      <div class="p-12 lg:p-16">
        <h2 class="text-[38px] font-black italic uppercase text-[#111] mb-10">{{ t('titles.forgotPassword') }}</h2>

        <form @submit.prevent="handleSubmit" class="space-y-5 max-w-[760px]">
          <!-- Email -->
          <div class="max-w-[520px]">
            <input id="mm-reset-email" ref="emailInput" v-model="email" type="email" autocomplete="email" required
                   :placeholder="t('placeholders.email')"
                   class="w-full h-12 bg-transparent px-1 border-0 border-b-2 border-[#d4cfc5] text-black placeholder:text-[#8c857a]
                  focus:outline-none focus:border-black" />
          </div>

          <!-- Cloudflare Turnstile -->
          <div class="max-w-[520px]">
            <div v-if="turnstileEnabled" class="border border-[#e5dfd3] rounded">
              <div ref="turnstile" class="mm-turnstile" />
              <p v-if="turnstileError" class="mt-2 text-[13px] text-[#b91c1c]">{{ turnstileError }}</p>
            </div>
            <div v-else class="flex items-center justify-between border border-[#e5dfd3] rounded bg-white p-3">
              <div class="flex items-center gap-2">
                <div class="w-5 h-5 grid place-items-center bg-[#55b36a] text-white text-[12px] font-bold rounded-sm">✓</div>
                <span class="text-[15px] text-[#333]">{{ t('messages.verificationDisabled') }}</span>
              </div>
              <span class="text-[11px] text-[#777]">TURNSTILE</span>
            </div>
          </div>

          <!-- Submit -->
          <button type="submit" :disabled="isLoading || !formValid"
                  class="w-full max-w-[520px] h-12 bg-black text-white italic uppercase font-semibold tracking-wide disabled:opacity-60">
            <span v-if="isLoading">{{ t('buttons.sending') }}</span><span v-else>{{ t('buttons.resetPassword') }}</span>
          </button>

          <!-- Back to login and signup links (plain text links) -->
          <div class="w-full max-w-[520px] flex items-center justify-between -mt-2">
            <button type="button" @click="handleBackToLogin"
                    class="text-left p-0 text-[15px] text-[#111] underline italic hover:text-black bg-transparent">
              {{ t('buttons.backToLogin') }}
            </button>
            <button type="button" @click="handleSignup"
                    class="text-left p-0 text-[15px] text-[#111] underline italic hover:text-black bg-transparent">
              {{ t('titles.register') }}
            </button>
          </div>

          <!-- Messages -->
          <MmFlashMessage
            v-if="errorMessage"
            :message="errorMessage"
            type="error"
            @dismiss="errorMessage = ''"
          />
          <MmFlashMessage
            v-if="successMessage"
            :message="successMessage"
            type="success"
            :autoHideMs="6000"
            @dismiss="successMessage = ''"
          />
        </form>
      </div>

      <!-- INFO PANEL -->
      <aside class="bg-gradient-to-br from-[#EA6730] to-[#F7941F] text-white p-10 lg:p-14 flex flex-col gap-8">
        <div>
          <h3 class="text-[26px] text-white italic uppercase mb-6 leading-tight">
            <span class="whitespace-pre-line">{{ t('marketing.pitchTitle') }}</span>
          </h3>
          <ul class="space-y-4 text-[16px]">
            <li class="flex items-center gap-3">
              <svg class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp1') }}</span>
            </li>
            <li class="flex items-center gap-3">
              <svg class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp2') }}</span>
            </li>
            <li class="flex items-center gap-3">
              <svg class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp3') }}</span>
            </li>
            <li class="flex items-center gap-3">
              <svg class="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M4 12.5l6 6L20 6" fill="none" stroke="#ffffff" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
              <span>{{ t('marketing.usp4') }}</span>
            </li>
          </ul>
        </div>
        <button @click="handleSignup" class="mt-4 w-full bg-white text-black italic uppercase font-extrabold h-12">
          {{ t('titles.register') }}
        </button>
      </aside>
    </div>
  </section>
</template>

<script>
import WpApiClient from '../lib/wp-api.js'
import Turnstile from '../lib/turnstile.js'
import { t as i18nT } from '../i18n'
import MmFlashMessage from './MmFlashMessage.vue'
export default {
  name: 'MmForgotPassword',
  emits: ['backToLogin', 'requestSignup'],
  components: { MmFlashMessage },
  data() {
    return {
      t: i18nT,
      email: '',
      isLoading: false,
      errorMessage: '',
      successMessage: '',
      // Turnstile
      turnstileEnabled: !!(Turnstile && Turnstile.isEnabled && Turnstile.isEnabled()),
      turnstileToken: '',
      turnstileWidgetId: null,
      turnstileError: '',
      api: null,
    };
  },
  mounted() {
    if (this.turnstileEnabled) {
      this.initTurnstile();
    }
    this.api = new WpApiClient();
  },
  computed: {
    formValid(){
      const emailOk = this.isValidEmail(this.email);
      const turnstileOk = (!this.turnstileEnabled) || (!!this.turnstileToken);
      return emailOk && turnstileOk;
    }
  },
  methods: {
    t(key){ return i18nT(key) },
    async initTurnstile() {
      this.turnstileError = '';
      if (!Turnstile.isEnabled()) { this.turnstileEnabled = false; return; }
      await Turnstile.waitForApi();
      if (!(window.turnstile && this.$refs.turnstile)) return;
      try {
        this.turnstileWidgetId = Turnstile.render(this.$refs.turnstile, {
          onToken: (token) => { this.turnstileToken = token; this.turnstileError=''; },
          onError: () => { this.turnstileToken = ''; this.turnstileError = this.t('messages.turnstileError'); },
          onExpired: () => { this.turnstileToken = ''; this.turnstileError = this.t('messages.turnstileExpired'); }
        });
      } catch (e) {
        this.turnstileError = this.t('messages.turnstileLoadFailed');
      }
    },
    resetTurnstile() {
      try {
        if (this.turnstileWidgetId) {
          Turnstile.reset(this.turnstileWidgetId);
        }
        this.turnstileToken = '';
      } catch (_) {}
    },
    isValidEmail(str){
      const s = String(str||'').trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s);
    },
    scrollToRef(refName){
      const el = this.$refs[refName];
      if (el && typeof el.scrollIntoView === 'function') {
        try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch(_) { el.scrollIntoView(); }
      }
      if (el && typeof el.focus === 'function') {
        try { el.focus({ preventScroll: true }); } catch(_) { try { el.focus(); } catch(__) {} }
      }
    },
    findFirstInvalid(){
      if (!this.isValidEmail(this.email)) return 'emailInput';
      if (this.turnstileEnabled && !this.turnstileToken) return 'turnstile';
      return '';
    },
    async handleSubmit() {
      // Client-side: smooth-scroll to first invalid and stop
      if (!this.formValid) {
        const ref = this.findFirstInvalid();
        if (ref) this.scrollToRef(ref);
        return;
      }
      this.isLoading = true; this.errorMessage = ''; this.successMessage = '';
      try {
        const result = await this.api.forgotPassword({
          email: this.email,
          nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined,
          turnstileToken: (this.turnstileEnabled && this.turnstileToken) ? this.turnstileToken : undefined,
        });
        if (result && result.ok) {
          this.successMessage = this.t('messages.forgotInfo');
        } else {
          this.errorMessage = (result && result.error) || this.t('messages.submitFailed');
          this.resetTurnstile();
        }
      } catch (e) {
        this.errorMessage = e?.message || this.t('messages.genericError');
        this.resetTurnstile();
      }
      this.isLoading = false;
    },
    handleBackToLogin() { this.$emit('backToLogin'); },
    handleSignup() { this.$emit('requestSignup'); }
  }
};
</script>
